import type { RuntimeLintContext, RuntimeLintRule } from './runtimeRules';
import type { ElementMeasurement, FrameSnapshot, Rect } from './measure';
import { describeElement, intersectRects, visibleThroughWindows } from './measure';
import { boxIsNotTheShape } from './clipRules';
import { isRoughlySquare, isStage, settledFrame } from './geometryRules';
import { lineOf, textOf } from './textLeaves';
import { parseColor } from './contrast';
import type { ImageLike } from './contrast';

/**
 * How two drawn things sit against each other over the scene. The vision reviewer, shown the same
 * frames, rated a tick ring turning off its face and a label lying across a bar as too minor to
 * report; measured, both are plain arithmetic on boxes the lint already has.
 */

const OPACITY_PRESENT_THRESHOLD = 0.05;
// Below this a shape reads as a wash behind the copy rather than something it collides with.
const SHAPE_OPACITY_MIN = 0.45;
// Copy is judged once it has arrived: mid-fade it is not yet readable and its box is still moving.
const TEXT_SETTLED_OPACITY = 0.9;

const DRIFT_MIN_SIZE_PX = 40;
// Centres this close are one centre, allowing for sub-pixel layout and transform rounding.
const CONCENTRIC_TOL_PX = 4;
// Centres this far apart no longer read as concentric.
const DRIFT_MIN_PX = 10;
// A shape that also changed size this much is entering or leaving, not turning around a bad origin.
const DRIFT_SIZE_TOL = 0.25;

const OVERLAP_MIN_PX = 8;
// A shape this slim on its short side is a rule, an axis or a divider rather than a surface.
const THIN_SHAPE_PX = 12;
// ...and this much longer than it is thick, so a dot or a small square marker is not read as one.
const HAIRLINE_ASPECT = 4;
// A shape containing the copy with at least this much room on every side is its backdrop.
const BACKDROP_PAD_PX = 4;
// A shape whose centre lands this close to the copy's is a ring, rule or lasso drawn around it.
const CENTRED_TOL_X = 0.1;
const CENTRED_TOL_Y = 0.25;
const CENTRED_TOL_PX = 4;

const ALLOW_OFFSET_ATTR = '[data-l5-allow-offset]';
const ALLOW_OVERLAP_ATTR = '[data-l5-allow-overlap]';

function centre(rect: Rect): { x: number; y: number } {
    return { x: rect.x + rect.width / 2, y: rect.y + rect.height / 2 };
}

function distance(a: Rect, b: Rect): number {
    const ca = centre(a);
    const cb = centre(b);
    return Math.hypot(ca.x - cb.x, ca.y - cb.y);
}

function related(a: Element, b: Element): boolean {
    return a.contains(b) || b.contains(a);
}

function sameSize(a: Rect, b: Rect): boolean {
    return (
        Math.abs(a.width - b.width) <= DRIFT_SIZE_TOL * a.width &&
        Math.abs(a.height - b.height) <= DRIFT_SIZE_TOL * a.height
    );
}

function intersection(a: Rect, b: Rect): Rect | null {
    const x = Math.max(a.x, b.x);
    const y = Math.max(a.y, b.y);
    const right = Math.min(a.x + a.width, b.x + b.width);
    const bottom = Math.min(a.y + a.height, b.y + b.height);
    return right > x && bottom > y ? { x, y, width: right - x, height: bottom - y } : null;
}

/**
 * Whether the segment from `p` to `q` touches `rect`. Sampled points alone miss a steep stroke
 * whose consecutive samples straddle a one-line caption, so the run between them is what is tested.
 */
function segmentCrossesRect(p: [number, number], q: [number, number], rect: Rect): boolean {
    const left = rect.x;
    const right = rect.x + rect.width;
    const top = rect.y;
    const bottom = rect.y + rect.height;
    const inside = ([x, y]: [number, number]) => x >= left && x <= right && y >= top && y <= bottom;
    if (inside(p) || inside(q)) return true;
    // Liang–Barsky: shrink the parametric interval by each of the four half-planes.
    const dx = q[0] - p[0];
    const dy = q[1] - p[1];
    let t0 = 0;
    let t1 = 1;
    for (const [numerator, denominator] of [
        [left - p[0], dx],
        [p[0] - right, -dx],
        [top - p[1], dy],
        [p[1] - bottom, -dy],
    ]) {
        if (denominator === 0) {
            if (numerator > 0) return false;
            continue;
        }
        const t = numerator / denominator;
        if (denominator > 0) t0 = Math.max(t0, t);
        else t1 = Math.min(t1, t);
        if (t0 > t1) return false;
    }
    return true;
}

function contains(outer: Rect, inner: Rect, pad: number): boolean {
    return (
        inner.x >= outer.x + pad &&
        inner.y >= outer.y + pad &&
        inner.x + inner.width <= outer.x + outer.width - pad &&
        inner.y + inner.height <= outer.y + outer.height - pad
    );
}

/**
 * Round-ish, big enough to have a centre worth holding, and on screen. Grouping elements stay in,
 * unlike in the shape rules: a ring of ticks is a `<g>`, and it is the group that turns around the
 * wrong origin — the same element is compared with itself across frames, so its box being what its
 * children span is exactly the point.
 */
function isDriftCandidate(
    m: ElementMeasurement,
    minOpacity: number,
    canvasSize?: { width: number; height: number },
) {
    return (
        !m.hasText &&
        m.opacity >= minOpacity &&
        m.rect.width >= DRIFT_MIN_SIZE_PX &&
        m.rect.height >= DRIFT_MIN_SIZE_PX &&
        isRoughlySquare(m.rect) &&
        !isStage(m.rect, canvasSize)
    );
}

/**
 * Two round shapes that share a centre in one frame and have parted by a later one, at unchanged
 * size. A ring's contents turning around the wrong transform origin look exactly like this, and
 * nothing else does: an exit changes size, a hand is not round, a deliberate orbit is marked.
 */
export const concentricPairDrift: RuntimeLintRule = {
    id: 'concentric-pair-drift',
    severity: 'error',
    guidance:
        'A shape that turns or moves inside a ring, dial or clock keeps the centre it started on. ' +
        'Give a rotating group its own centre as its transform origin (`svgOrigin` for SVG) rather ' +
        'than the default, and check the last frames as well as the first — or mark the shape ' +
        'data-l5-allow-offset if it is meant to travel away.',
    check(context: RuntimeLintContext): string[] {
        const messages: string[] = [];
        const frames = context.frames ?? [];
        const reported = new Set<string>();
        for (let i = 0; i < frames.length; i++) {
            // The baseline comes from settled shapes: an expand-from-centre entrance is concentric
            // with its core while still faint, and that is an arrival, not a drift.
            const early = frames[i].elements.filter((m) =>
                isDriftCandidate(m, SHAPE_OPACITY_MIN, context.canvasSize),
            );
            for (let a = 0; a < early.length; a++) {
                for (let b = a + 1; b < early.length; b++) {
                    const first = early[a];
                    const second = early[b];
                    if (related(first.element, second.element)) continue;
                    if (distance(first.rect, second.rect) > CONCENTRIC_TOL_PX) continue;
                    if (
                        first.element.closest(ALLOW_OFFSET_ATTR) ||
                        second.element.closest(ALLOW_OFFSET_ATTR)
                    ) {
                        continue;
                    }
                    const key = [describeElement(first.element), describeElement(second.element)]
                        .sort()
                        .join('|');
                    if (reported.has(key)) continue;

                    const parted = frames.slice(i + 1).find((later: FrameSnapshot) => {
                        const laterFirst = later.elements.find((m) => m.element === first.element);
                        const laterSecond = later.elements.find(
                            (m) => m.element === second.element,
                        );
                        if (!laterFirst || !laterSecond) return false;
                        if (
                            laterFirst.opacity < OPACITY_PRESENT_THRESHOLD ||
                            laterSecond.opacity < OPACITY_PRESENT_THRESHOLD
                        ) {
                            return false;
                        }
                        if (
                            !sameSize(first.rect, laterFirst.rect) ||
                            !sameSize(second.rect, laterSecond.rect)
                        ) {
                            return false;
                        }
                        return distance(laterFirst.rect, laterSecond.rect) >= DRIFT_MIN_PX;
                    });
                    if (!parted) continue;

                    reported.add(key);
                    const laterFirst = parted.elements.find((m) => m.element === first.element)!;
                    const laterSecond = parted.elements.find((m) => m.element === second.element)!;
                    const apart = Math.round(distance(laterFirst.rect, laterSecond.rect));
                    messages.push(
                        `${describeElement(first.element)} and ${describeElement(second.element)} ` +
                            `share a centre at progress ${frames[i].progress} but are ${apart}px ` +
                            `apart at progress ${parted.progress} — one of them is turning or ` +
                            `moving around a point that is not its own centre. Give it its centre ` +
                            `as its transform origin (svgOrigin for SVG), or mark it ` +
                            `data-l5-allow-offset if it is meant to travel away.`,
                    );
                }
            }
        }
        return messages;
    },
};

/** Whether the element carries copy anywhere inside it — a card, a pill, a panel with a label. */
function holdsCopy(element: Element): boolean {
    return (element.textContent ?? '').trim().length > 0;
}

const SVG_NAMESPACE = 'http://www.w3.org/2000/svg';
const REPLACED_TAGS = new Set(['IMG', 'VIDEO', 'CANVAS', 'PICTURE']);
const TRANSPARENT = /^(?:transparent|rgba\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*,\s*0\s*\))?$/;

/**
 * Whether an HTML box puts any paint of its own on screen. A flex row holding the bars, or a
 * positioning wrapper around a chart, has a box but no edge a viewer can see, so a label lying
 * across it is not lying across anything. SVG graphics paint by definition; SVG grouping elements
 * are ruled out before this is asked.
 */
function paintsItself(element: Element): boolean {
    if (element.namespaceURI === SVG_NAMESPACE) return true;
    if (REPLACED_TAGS.has(element.tagName.toUpperCase())) return true;
    const inline = (element as HTMLElement).style;
    const computed = element.ownerDocument?.defaultView?.getComputedStyle(element);
    const value = (property: string): string =>
        (inline?.getPropertyValue(property) || computed?.getPropertyValue(property) || '').trim();
    if (!TRANSPARENT.test(value('background-color'))) return true;
    const image = value('background-image');
    if (image && image !== 'none') return true;
    const shadow = value('box-shadow');
    if (shadow && shadow !== 'none') return true;
    return [
        'border-top-width',
        'border-right-width',
        'border-bottom-width',
        'border-left-width',
    ].some((side) => parseFloat(value(side)) > 0);
}

/**
 * A shape whose edge runs through a line of copy. A card behind a label contains the copy with room
 * to spare and a ring or rule drawn around a figure is centred on it; a bar with a value label lying
 * across it, or a circle a caption disappears behind, is neither. Text is judged once it has
 * settled; a transition that sweeps a cover over settled copy on purpose carries
 * `data-l5-allow-overlap` on the cover, as the slat-wipe skill does.
 */
export const textOverShape: RuntimeLintRule = {
    id: 'text-over-shape',
    severity: 'warning',
    guidance:
        'Keep copy clear of the shapes around it: a label lying across a bar, or a caption a shape ' +
        'grows over, reads as a collision. A card or pill behind the copy, or a ring drawn around ' +
        'it, is fine. Add data-l5-allow-overlap to the copy or the shape if the layering is the ' +
        'design.',
    check(context: RuntimeLintContext): string[] {
        const messages: string[] = [];
        const reportedPairs = new Set<string>();
        const onCanvas = (rect: Rect): Rect =>
            context.canvasSize ? intersectRects(rect, { x: 0, y: 0, ...context.canvasSize }) : rect;
        for (const frame of context.frames ?? []) {
            const copy = frame.elements.filter(
                (m) => m.hasText && m.opacity >= TEXT_SETTLED_OPACITY,
            );
            // Only a box a viewer can see an edge of: not a grouping element, an undrawn or cropped
            // shape (`boxIsNotTheShape`), or an HTML wrapper with no paint of its own. Rules, axes
            // and dividers belong to `text-crossed-by-rule`, which blocks rather than warns —
            // between 8 and 12px thick both could otherwise report the same pair.
            const shapes = frame.elements.filter(
                (m) =>
                    !m.hasText &&
                    m.opacity >= SHAPE_OPACITY_MIN &&
                    !boxIsNotTheShape(m) &&
                    paintsItself(m.element) &&
                    !holdsCopy(m.element) &&
                    !isStage(m.rect, context.canvasSize) &&
                    !isHairline(paintedRect(m.element, m.rect)),
            );
            for (const text of copy) {
                const ink = onCanvas(
                    visibleThroughWindows(
                        text.glyphRect ?? text.inkRect,
                        text.clipRect,
                        context.canvasSize,
                    ),
                );
                if (ink.width <= 0 || ink.height <= 0) continue;
                if (text.element.closest(ALLOW_OVERLAP_ATTR)) continue;
                for (const shape of shapes) {
                    if (related(text.element, shape.element)) continue;
                    if (shape.element.closest(ALLOW_OVERLAP_ATTR)) continue;
                    const drawn = onCanvas(
                        visibleThroughWindows(shape.rect, shape.clipRect, context.canvasSize),
                    );
                    if (drawn.width <= 0 || drawn.height <= 0) continue;
                    const overlap = intersection(ink, drawn);
                    if (
                        !overlap ||
                        overlap.width < OVERLAP_MIN_PX ||
                        overlap.height < OVERLAP_MIN_PX
                    ) {
                        continue;
                    }
                    // A stroke is judged where it is drawn, not by the box it could reach: a curve
                    // rising past the headline crosses it only if a sampled point lands in the copy.
                    if (shape.outline) {
                        const crosses = shape.outline.some((run) =>
                            run.some(
                                (point, index) =>
                                    index > 0 && segmentCrossesRect(run[index - 1], point, ink),
                            ),
                        );
                        if (!crosses) continue;
                    } else if (contains(drawn, ink, BACKDROP_PAD_PX)) {
                        continue;
                    }
                    const ci = centre(ink);
                    const cs = centre(drawn);
                    if (
                        Math.abs(ci.x - cs.x) <= CENTRED_TOL_X * ink.width + CENTRED_TOL_PX &&
                        Math.abs(ci.y - cs.y) <= CENTRED_TOL_Y * ink.height + CENTRED_TOL_PX
                    ) {
                        continue;
                    }
                    const key = `${describeElement(text.element)}|${describeElement(shape.element)}`;
                    if (reportedPairs.has(key)) continue;
                    reportedPairs.add(key);
                    messages.push(
                        `${describeElement(text.element)} is crossed by ` +
                            `${describeElement(shape.element)} at progress ${frame.progress} ` +
                            `(${Math.round(overlap.width)}×${Math.round(overlap.height)}px of the ` +
                            `copy). Move one clear of the other, or add data-l5-allow-overlap to ` +
                            `either if the layering is the design.`,
                    );
                }
            }
        }
        return messages;
    },
};

/**
 * The box a shape actually paints, which for stroked SVG geometry is not the box it measures.
 *
 * Chrome leaves the stroke out of `getBoundingClientRect()`: measured in headless Chrome, a
 * `<line stroke-width="3">` across a chart reports a height of **0**, and a stroked curve reports
 * 9.58px while painting 15.58px. A zero-height box is dropped as undrawn before any thinness test
 * runs, so an axis authored the way `chart-story-build` teaches it — an SVG `<line>` — was invisible
 * to this rule while a `<div>` axis was caught. The stroke is measured in user units, so it is
 * carried into screen space by the CTM the same way the sampled outline is.
 */
function paintedRect(element: Element, rect: Rect): Rect {
    const view = element.ownerDocument?.defaultView;
    const geometry = element as unknown as { getScreenCTM?: () => DOMMatrix | null };
    if (!view || typeof geometry.getScreenCTM !== 'function') return rect;
    const style = view.getComputedStyle(element);
    // The resolved style first and the presentation attribute only as a fallback, the way the
    // cascade works: a stylesheet beats an attribute of specificity 0.
    const paint = (style.stroke || element.getAttribute('stroke') || '').trim();
    if (!paint || paint === 'none' || /^(rgba\(.*,\s*0\)|transparent)$/.test(paint)) return rect;
    const width = parseFloat(style.strokeWidth || element.getAttribute('stroke-width') || '');
    if (!Number.isFinite(width) || width <= 0) return rect;
    let scale = 1;
    try {
        const ctm = geometry.getScreenCTM?.();
        if (ctm) scale = Math.hypot(ctm.a, ctm.b) || 1;
    } catch {
        return rect;
    }
    const grow = (width * scale) / 2;
    return {
        x: rect.x - grow,
        y: rect.y - grow,
        width: rect.width + grow * 2,
        height: rect.height + grow * 2,
    };
}

/** A rule, axis or divider: thin enough to be a line, and long enough not to be a dot. */
function isHairline(rect: Rect): boolean {
    const short = Math.min(rect.width, rect.height);
    const long = Math.max(rect.width, rect.height);
    return short <= THIN_SHAPE_PX && long >= short * HAIRLINE_ASPECT;
}

/**
 * The band a strike-through has to cross: the letterforms themselves, from the top of the ink down
 * to the baseline.
 *
 * An ink box runs to the bottom of the deepest descender, and the band below the baseline is mostly
 * empty — it is where an underline goes. Measured on two real runs, the accent rule the skills draw
 * beneath a headline lands squarely in it: a 4px curve under "Update" was reported as drawn through
 * the copy because the `p` made the box reach down to meet it. Judging the body alone keeps an
 * underline an underline, and still catches a rule laid across the letterforms.
 */
function letterformBody(ink: Rect, baselineY: number | undefined): Rect {
    if (baselineY === undefined || !Number.isFinite(baselineY)) return ink;
    const bottom = Math.min(ink.y + ink.height, baselineY);
    return { x: ink.x, y: ink.y, width: ink.width, height: bottom - ink.y };
}

/**
 * The rule for a hairline drawn through a line of copy.
 *
 * `text-over-shape` cannot see this one, and not by oversight: it asks for the overlap to clear
 * `OVERLAP_MIN_PX` on *both* axes, and an axis six pixels tall can never overlap anything by eight
 * pixels vertically. So every rule, axis and divider is invisible to it no matter how squarely it
 * crosses a number. The defect that prompted this shipped a 6px axis running through the caps of a
 * `$11M` label, with the lint reporting only the bar behind it.
 *
 * Where that rule warns, this one blocks. A card behind a label or a bar under a value is a
 * layering decision an author can reasonably make, which is why it stays a warning; a hairline
 * through the middle of a figure is never the design, and it reads as a strike-through.
 *
 * It measures the glyphs rather than the line box, because the thing that makes a rule look like it
 * is touching a number is where the ink is, and a line box at `line-height: 1.05` carries several
 * pixels of leading that would otherwise count as a collision. The overlap is required along the
 * rule's long axis only; across its short axis any overlap at all is the whole of it.
 */
export const textCrossedByRule: RuntimeLintRule = {
    id: 'text-crossed-by-rule',
    severity: 'error',
    guidance:
        'Keep a rule, axis or divider out of the copy near it. A value label sits clear of the ' +
        'baseline it belongs to, not on it — and `top` positions the line box, not the ink, so a ' +
        'label placed at the axis coordinate lands on the rule rather than under it. Move one ' +
        'clear of the other, or add data-l5-allow-overlap if the copy is genuinely meant to be ' +
        'struck through.',
    check(context: RuntimeLintContext): string[] {
        const frame = settledFrame(context);
        if (!frame) return [];

        const copy = frame.elements.filter(
            (m) => m.hasText && m.glyphRect && m.opacity >= TEXT_SETTLED_OPACITY,
        );
        const rules = frame.elements
            .map((m) => ({ measured: m, painted: paintedRect(m.element, m.rect) }))
            .filter(
                ({ measured, painted }) =>
                    !measured.hasText &&
                    measured.opacity >= SHAPE_OPACITY_MIN &&
                    // Judged on the painted box, so a hairline whose measured box has no height at
                    // all is not discarded as undrawn before it can be looked at.
                    !boxIsNotTheShape({ ...measured, rect: painted }) &&
                    paintsItself(measured.element) &&
                    !isStage(painted, context.canvasSize) &&
                    isHairline(painted),
            );

        const messages: string[] = [];
        const reportedPairs = new Set<string>();
        for (const text of copy) {
            // Only the part of the copy that survives the boxes cropping it: a wheel's digit strip
            // carries thirty digits and all but the one in its window are scrolled out of sight,
            // so its unclipped ink crosses everything on the frame.
            const visible = text.clipRect
                ? intersectRects(text.glyphRect!, text.clipRect)
                : text.glyphRect!;
            const ink = letterformBody(visible, text.baselineY);
            if (ink.width <= 0 || ink.height <= 0) continue;
            if (text.element.closest(ALLOW_OVERLAP_ATTR)) continue;
            for (const { measured: shape, painted } of rules) {
                if (related(text.element, shape.element)) continue;
                if (shape.element.closest(ALLOW_OVERLAP_ATTR)) continue;
                // The shape gets the same treatment as the copy: a separator inside a wheel's
                // window is thirty digits long as a box while a few pixels of it are on screen.
                const drawn = shape.clipRect ? intersectRects(painted, shape.clipRect) : painted;
                if (drawn.width <= 0 || drawn.height <= 0) continue;
                if (!isHairline(drawn)) continue;
                const overlap = intersection(ink, drawn);
                if (!overlap || overlap.width <= 0 || overlap.height <= 0) continue;
                // Along the rule's length the crossing has to be substantial; across its thickness
                // it cannot be, which is the reason this rule exists.
                const horizontal = drawn.width >= drawn.height;
                const along = horizontal ? overlap.width : overlap.height;
                if (along < OVERLAP_MIN_PX) continue;

                const key = `${describeElement(text.element)}|${describeElement(shape.element)}`;
                if (reportedPairs.has(key)) continue;
                reportedPairs.add(key);
                messages.push(
                    `${describeElement(shape.element)} is drawn through the glyphs of ` +
                        `${describeElement(text.element)} — ${Math.round(along)}px along it, ` +
                        `${Math.round(horizontal ? overlap.height : overlap.width)}px into the ` +
                        `copy — so the copy reads as struck through. Move the copy clear of the ` +
                        `rule, remembering that \`top\` places the line box and the glyphs sit ` +
                        `lower inside it, or add data-l5-allow-overlap if this is the design.`,
                );
            }
        }
        return messages;
    },
};

// Below this opacity the copy is on its way in or out, not yet something the viewer is reading.
const COVERED_COPY_LEGIBLE_OPACITY = 0.5;
// The share of a line a layer must hide for the line to stop reading: less is a corner clipped.
const COVERED_SHARE_MIN = 0.5;
// Manhattan distance within which a rendered pixel reads as the cover's own paint.
const COVER_PAINT_DISTANCE = 48;

async function rasterOf(frame: FrameSnapshot): Promise<ImageLike | null> {
    try {
        const canvas = await frame.rasterize();
        return canvas.getContext('2d')?.getImageData(0, 0, canvas.width, canvas.height) ?? null;
    } catch {
        return null;
    }
}

/**
 * Whether the rendered frame shows the cover's paint, or the copy it draws, where the copy was
 * measured under it. The measured pose can disagree with what is drawn, and a block on a cover
 * nobody sees cannot be fixed. Without a raster, or a flat paint to look for, the measurement stands.
 */
function frameShowsCover(
    text: ElementMeasurement,
    image: ImageLike | null,
    canvasSize: RuntimeLintContext['canvasSize'],
): boolean {
    const paints = (text.coveredBy?.paints ?? [])
        .map(parseColor)
        .filter((paint): paint is NonNullable<typeof paint> => !!paint);
    const points = text.coveredBy?.points ?? [];
    if (!image || paints.length === 0 || points.length === 0 || !canvasSize) return true;
    const scaleX = image.width / canvasSize.width;
    const scaleY = image.height / canvasSize.height;
    const painted = points.filter(([x, y]) => {
        const px = Math.min(image.width - 1, Math.max(0, Math.floor(x * scaleX)));
        const py = Math.min(image.height - 1, Math.max(0, Math.floor(y * scaleY)));
        const at = (py * image.width + px) * 4;
        return paints.some(
            (paint) =>
                Math.abs(image.data[at] - paint.r) +
                    Math.abs(image.data[at + 1] - paint.g) +
                    Math.abs(image.data[at + 2] - paint.b) <
                COVER_PAINT_DISTANCE,
        );
    });
    return painted.length * 2 >= points.length;
}

/**
 * Copy on screen with an opaque layer painted over it: a panel or the next beat's ground left
 * standing over the copy this beat is meant to show. `text-over-shape` takes a shape that holds the
 * whole line for the card behind it; this asks which of the two is on top. Copy the viewer already
 * read uncovered is left alone once a later beat's ground cuts over it.
 */
export const textCoveredByLayer: RuntimeLintRule = {
    id: 'text-covered',
    severity: 'error',
    guidance:
        'Never leave a layer painted over copy that is on screen: a panel, a ground or an image ' +
        'stacked above a line hides it. Keep each layer hidden until its own beat. Add ' +
        'data-l5-allow-overlap to a cover that wipes over the copy on purpose.',
    async check(context: RuntimeLintContext): Promise<string[]> {
        const messages: string[] = [];
        const reported = new Set<string>();
        const readUncovered = new Set<string>();
        const isCandidate = (text: ElementMeasurement): boolean => {
            const cover = text.coveredBy;
            return (
                !!cover &&
                cover.share >= COVERED_SHARE_MIN &&
                !text.element.closest(ALLOW_OVERLAP_ATTR) &&
                !cover.element.closest(ALLOW_OVERLAP_ATTR)
            );
        };
        const showsInk = (text: ElementMeasurement): boolean => {
            const ink = visibleThroughWindows(
                text.glyphRect ?? text.inkRect,
                text.clipRect,
                context.canvasSize,
            );
            const shown = context.canvasSize
                ? intersectRects(ink, { x: 0, y: 0, ...context.canvasSize })
                : ink;
            return shown.width > 0 && shown.height > 0;
        };
        for (const frame of context.frames ?? []) {
            const leavesByLine = new Map<Element, ElementMeasurement[]>();
            for (const text of frame.elements) {
                if (!text.hasText || text.opacity < COVERED_COPY_LEGIBLE_OPACITY) continue;
                if (text.element.closest('[data-l5-text-role="ambient"]')) continue;
                const line = lineOf(text.element);
                leavesByLine.set(line, [...(leavesByLine.get(line) ?? []), text]);
            }
            const image = [...leavesByLine.values()].flat().some(isCandidate)
                ? await rasterOf(frame)
                : null;
            const isHidden = (text: ElementMeasurement): boolean =>
                isCandidate(text) && frameShowsCover(text, image, context.canvasSize);
            const readThisFrame: string[] = [];
            for (const [line, leaves] of leavesByLine) {
                const lineText = textOf(line);
                const hidden = leaves.filter(isHidden);
                if (hidden.length === 0) {
                    if (leaves.some(showsInk)) readThisFrame.push(lineText);
                    continue;
                }
                if (reported.has(lineText) || readUncovered.has(lineText)) continue;
                reported.add(lineText);
                const copy =
                    hidden.length === leaves.length
                        ? `"${lineText}"`
                        : `${hidden.map((leaf) => `"${textOf(leaf.element)}"`).join(', ')} of "${lineText}"`;
                const [worst] = [...hidden].sort(
                    (a, b) => (b.coveredBy?.share ?? 0) - (a.coveredBy?.share ?? 0),
                );
                const cover = worst.coveredBy!;
                messages.push(
                    `${copy} (${describeElement(line)}) is hidden under ` +
                        `${describeElement(cover.element)} at progress ${frame.progress}: ` +
                        `${Math.round(cover.share * 100)}% of it is painted over, so the viewer ` +
                        `cannot read it. Keep the cover hidden until this copy has gone, or move ` +
                        `it clear. If the cover wipes over the copy on purpose, add ` +
                        `data-l5-allow-overlap to it.`,
                );
            }
            readThisFrame.forEach((text) => readUncovered.add(text));
        }
        return messages;
    },
};

export const CORE_SHAPE_RELATION_LINT_RULES: RuntimeLintRule[] = [
    concentricPairDrift,
    textOverShape,
    textCoveredByLayer,
    textCrossedByRule,
];
